// import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Toolbar,
  Typography,
  styled,
  useTheme,
  Box,
  Tooltip,
  IconButton,
  alpha,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  // Settings as SettingsIcon,
  // BarChart as ReportsIcon,
  Menu as MenuIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
// import { decryptData } from "utils/crypto";
import { useTranslation } from "react-i18next";

const DRAWER_WIDTH = 280;
const isDevelopmentMode = process.env.REACT_APP_ISDEVELOPMENT === "true";

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    width: DRAWER_WIDTH,
    boxSizing: "border-box",
    borderRight: "none",
    background: theme.palette.background.paper,
    boxShadow: theme.shadows[3],
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: theme.shadows[6],
    },
  },
}));

const StyledListItemButton = styled(ListItemButton)(({ theme, selected }) => ({
  borderRadius: 12,
  margin: theme.spacing(0, 1.5),
  padding: theme.spacing(1, 2),
  transition: "all 0.2s ease",
  backgroundColor: selected ? theme.palette.primary.light : "transparent",
  "&:hover": {
    backgroundColor: selected
      ? theme.palette.primary.light
      : theme.palette.action.hover,
    transform: "translateX(5px)",
    boxShadow: theme.shadows[1],
  },
  "& .MuiListItemIcon-root": {
    minWidth: "40px",
    color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
  },
  "& .MuiListItemText-primary": {
    fontWeight: selected ? 600 : 500,
    color: selected ? theme.palette.primary.main : theme.palette.text.primary,
  },
}));

const DashboardDrawer = ({
  mobileOpen,
  handleDrawerToggle,
  isMobile,
  sidebarOpen,
  setSidebarOpen,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  // const [userData, setUserData] = useState(null);
  const { t } = useTranslation();

  const menuGroups = [
    {
      title: t("Dashboard_menu.overview"),
      items: [
        {
          text: "Dashboard",
          icon: <DashboardIcon />,
          path: "/dashboard",
          disabled: false,
        },
      ],
    },
    {
      title: t("Dashboard_menu.application"),
      items: [
        {
          text: "Task Connect",
          icon: (
            <img
              src="/assets/icon/tc-icon.png"
              alt="Icon"
              style={{ width: 24, height: 24 }}
            />
          ),
          path: "/dashboard/task-connect",
          disabled: false,
        },
        {
          text: "Wage Connect",
          icon: (
            <img
              src="/assets/icon/wage-connect.png"
              alt="Icon"
              style={{ width: 24, height: 24 }}
            />
          ),
          path: "/dashboard/wage-connect",
          disabled: false,
        },
      ],
    },
    // {
    //   title: t("Dashboard_menu.analytics"),
    //   items: [
    //     {
    //       text: t("Dashboard_menu.reports"),
    //       icon: <ReportsIcon />,
    //       path: "/dashboard/reports",
    //       disabled: false,
    //     },
    //   ],
    // },
    // {
    //   title: t("Dashboard_menu.settings_group"),
    //   items: [
    //     {
    //       text: t("Dashboard_menu.settings"),
    //       icon: <SettingsIcon />,
    //       path: "/dashboard/settings",
    //       disabled: false,
    //     },
    //   ],
    // },
  ];

  // useEffect(() => {
  //   const storedUser = localStorage.getItem("user");
  //   if (storedUser) {
  //     try {
  //       const decryptedUser = decryptData(storedUser);
  //       setUserData(decryptedUser);
  //     } catch (error) {
  //       console.error("Failed to parse user data:", error);
  //     }
  //   }
  // }, []);

  return (
    <>
      {!isMobile && (
        <IconButton
          onClick={() => setSidebarOpen(!sidebarOpen)}
          sx={{
            position: "fixed",
            top: 16,
            left: sidebarOpen ? DRAWER_WIDTH + 16 : 16,
            zIndex: theme.zIndex.drawer + 1,
            backgroundColor: "background.paper",
            transition: "left 0.3s ease",
            "&:hover": {
              backgroundColor: "background.default",
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      <StyledDrawer
        variant={isMobile ? "temporary" : "persistent"}
        open={isMobile ? mobileOpen : sidebarOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
      >
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
            flexDirection: "column",
            gap: 1,
          }}
        >
          <img
            src="/images/joglo.png"
            alt="Logo"
            style={{
              width: "100%",
              maxWidth: "150px",
              height: "auto",
            }}
          />
        </Toolbar>

        <Divider
          sx={{ mx: 2, borderColor: theme.palette.divider, opacity: 0.5 }}
        />

        <Box
          sx={{
            overflowY: "auto",
            overflowX: "hidden",
            height: "calc(100vh - 150px)",
          }}
        >
          {menuGroups.map((group) => (
            <Box key={group.title} sx={{ mt: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  px: 3,
                  pb: 1,
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {group.title}
              </Typography>

              <List disablePadding>
                {group.items.map((item) => {
                  const isSelected =
                    location.pathname === item.path ||
                    (item.path !== "/dashboard" &&
                      location.pathname.startsWith(item.path));

                  const isInDev = isDevelopmentMode && item.isDevelopment;

                  return (
                    <motion.div
                      key={item.text}
                      whileHover={!item.disabled ? { scale: 1.02 } : {}}
                      whileTap={!item.disabled ? { scale: 0.98 } : {}}
                    >
                      <Tooltip
                        title={
                          isInDev
                            ? t("coming_soon_tooltip", "Coming Soon")
                            : item.disabled
                            ? "Please subscribe to activate this feature"
                            : ""
                        }
                        arrow
                        disableHoverListener={!(item.disabled || isInDev)}
                      >
                        <ListItem disablePadding sx={{ my: 0.5 }}>
                          <StyledListItemButton
                            selected={isSelected}
                            onClick={() =>
                              !item.disabled && navigate(item.path)
                            }
                            disabled={item.disabled}
                            sx={{
                              opacity: item.disabled ? 0.5 : 1,
                              pointerEvents: item.disabled ? "none" : "auto",
                              "&.Mui-disabled": {
                                color: theme.palette.text.disabled,
                              },
                              ...(isInDev && {
                                borderLeft: `3px solid ${theme.palette.warning.main}`,
                                backgroundColor: alpha(
                                  theme.palette.warning.light,
                                  0.1
                                ),
                              }),
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                color: item.disabled
                                  ? theme.palette.text.disabled
                                  : "inherit",
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={item.text}
                              primaryTypographyProps={{
                                variant: "body2",
                                color: item.disabled
                                  ? "text.disabled"
                                  : "inherit",
                              }}
                            />
                            {isSelected && !item.disabled && (
                              <Box
                                component="span"
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: theme.palette.primary.main,
                                  ml: 1,
                                }}
                              />
                            )}
                          </StyledListItemButton>
                        </ListItem>
                      </Tooltip>
                    </motion.div>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>

        {/* <Box sx={{ p: 2, mt: "auto" }}>
          <Divider sx={{ mb: 2, mx: 2 }} />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              p: 1.5,
              borderRadius: 2,
              backgroundColor: theme.palette.action.hover,
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: theme.palette.action.selected,
              },
            }}
          >
            <Avatar
              src="/static/images/avatar/1.jpg"
              sx={{ width: 36, height: 36, mr: 1.5 }}
            />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {userData?.userFullName || "User"}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary }}
              >
                {userData?.email || "email"}
              </Typography>
            </Box>
          </Box>
        </Box> */}
      </StyledDrawer>
    </>
  );
};

export default DashboardDrawer;
